// =============================================================================
// PAGES/IMPORTAR-PLOOMES.TSX — Integração "Importar do Ploomes" (admin)
// =============================================================================
// Fluxo preview → confirmar → progresso. Carrega o preview (o que entraria vs.
// o que já existe no CRM — dedup por ploomesDealId/idCstimob/offerId), o operador
// confirma, e a carga roda em background com barra de progresso (polling do job).
// =============================================================================
import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, RefreshCw, CheckCircle2, AlertTriangle, Loader2, Paperclip } from 'lucide-react';
import { integracoesApi, type PloomesPreview, type PloomesJob, type PloomesAnexosPreview, type PloomesAnexosJob } from '../services/api';

const ORIGEM_LABEL: Record<string, string> = { QUINTO_ANDAR: '5A (Quinto Andar)', S_QUATRO: 'S4 (Construtora)', VAREJO: 'VAR (Varejo)' };

export default function ImportarPloomes() {
  const navigate = useNavigate();
  const [preview, setPreview] = useState<PloomesPreview | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [job, setJob] = useState<PloomesJob | null>(null);
  const [importando, setImportando] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const carregarPreview = useCallback(async () => {
    setCarregando(true); setErro(null);
    try { setPreview(await integracoesApi.ploomesPreview()); }
    catch (e) { setErro((e as Error)?.message ?? 'Falha ao carregar o preview do Ploomes.'); }
    finally { setCarregando(false); }
  }, []);

  useEffect(() => { carregarPreview(); return () => { if (pollRef.current) clearInterval(pollRef.current); }; }, [carregarPreview]);

  async function importar() {
    if (!preview || !preview.novos) return;
    setImportando(true); setErro(null);
    try {
      const { jobId } = await integracoesApi.ploomesImportar();
      pollRef.current = setInterval(async () => {
        try {
          const j = await integracoesApi.ploomesJob(jobId);
          setJob(j);
          if (j.status !== 'rodando') {
            if (pollRef.current) clearInterval(pollRef.current);
            setImportando(false);
            if (j.status === 'concluido') carregarPreview();
          }
        } catch { /* mantém o polling */ }
      }, 2000);
    } catch (e) { setErro((e as Error)?.message ?? 'Falha ao iniciar a importação.'); setImportando(false); }
  }

  const pct = job && job.total ? Math.round(((job.feitos + job.erros) / job.total) * 100) : 0;

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-otm-txt-3 hover:text-otm-txt-1 mb-4">
        <ArrowLeft size={16} /> Voltar
      </button>

      <div className="bg-white rounded-2xl border border-otm-border shadow-sm overflow-hidden">
        <div className="px-6 py-5 border-b border-otm-border flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-otm-txt-1 flex items-center gap-2"><Download size={18} className="text-otm-orange" /> Importar do Ploomes</h1>
            <p className="text-sm text-otm-txt-3 mt-0.5">Traz as negociações do CRM legado (Ploomes). Registros já existentes no CRM são ignorados.</p>
          </div>
          <button onClick={carregarPreview} disabled={carregando || importando} className="text-otm-txt-3 hover:text-otm-txt-1 disabled:opacity-40" title="Recarregar preview">
            <RefreshCw size={18} className={carregando ? 'animate-spin' : ''} />
          </button>
        </div>

        <div className="p-6">
          {erro && <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"><AlertTriangle size={16} className="mt-0.5 shrink-0" />{erro}</div>}

          {carregando && !preview ? (
            <div className="flex items-center gap-2 text-otm-txt-3 py-8 justify-center"><Loader2 size={18} className="animate-spin" /> Analisando o Ploomes…</div>
          ) : preview ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                <Card n={preview.totalPloomes} label="Total no Ploomes" />
                <Card n={preview.jaImportados} label="Já importados" sub />
                <Card n={preview.jaExistemNoCrm} label="Já existem no CRM" sub />
                <Card n={preview.novos} label="Novos a importar" destaque />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <Bloco titulo="Novos por origem" dados={preview.porOrigem} rotulos={ORIGEM_LABEL} />
                <Bloco titulo="Novos por status" dados={preview.porStatus} />
              </div>

              {job && (
                <div className="mb-6 rounded-lg border border-otm-border bg-otm-subtle/40 p-4">
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="font-medium text-otm-txt-1 flex items-center gap-1.5">
                      {job.status === 'rodando' && <Loader2 size={15} className="animate-spin text-otm-orange" />}
                      {job.status === 'concluido' && <CheckCircle2 size={15} className="text-green-600" />}
                      {job.status === 'erro' && <AlertTriangle size={15} className="text-red-600" />}
                      {job.status === 'rodando' ? 'Importando…' : job.status === 'concluido' ? 'Importação concluída' : 'Importação com erro'}
                    </span>
                    <span className="text-otm-txt-3">{job.feitos + job.erros}/{job.total}{job.erros ? ` · ${job.erros} erro(s)` : ''}</span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-otm-border overflow-hidden">
                    <div className={`h-full ${job.status === 'erro' ? 'bg-red-500' : 'bg-otm-orange'} transition-all`} style={{ width: `${pct}%` }} />
                  </div>
                  {job.status === 'concluido' && <p className="text-xs text-otm-txt-3 mt-2">{job.feitos} importada(s) com sucesso{job.erros ? `, ${job.erros} falha(s)` : ''}.</p>}
                </div>
              )}

              <button
                onClick={importar}
                disabled={importando || !preview.novos}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-lg bg-otm-orange px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {importando ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                {importando ? 'Importando…' : preview.novos ? `Importar ${preview.novos} novo(s)` : 'Nada novo a importar'}
              </button>
            </>
          ) : null}
        </div>
      </div>

      <ImportarAnexos />
    </div>
  );
}

// ── Importar Anexos (Ploomes Blob → SharePoint "Histórico Ploomes/") ──────────
function ImportarAnexos() {
  const [prev, setPrev] = useState<PloomesAnexosPreview | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [incluirMidia, setIncluirMidia] = useState(false);
  const [job, setJob] = useState<PloomesAnexosJob | null>(null);
  const [importando, setImportando] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true); setErro(null);
    try { setPrev(await integracoesApi.anexosPreview()); }
    catch (e) { setErro((e as Error)?.message ?? 'Falha ao carregar o preview de anexos.'); }
    finally { setCarregando(false); }
  }, []);

  useEffect(() => { carregar(); return () => { if (pollRef.current) clearInterval(pollRef.current); }; }, [carregar]);

  async function importar() {
    if (!prev || !prev.semAnexosAinda) return;
    setImportando(true); setErro(null);
    try {
      const { jobId } = await integracoesApi.anexosImportar(incluirMidia);
      pollRef.current = setInterval(async () => {
        try {
          const j = await integracoesApi.anexosJob(jobId);
          setJob(j);
          if (j.status !== 'rodando') {
            if (pollRef.current) clearInterval(pollRef.current);
            setImportando(false);
            if (j.status === 'concluido') carregar();
          }
        } catch { /* mantém o polling */ }
      }, 2000);
    } catch (e) { setErro((e as Error)?.message ?? 'Falha ao iniciar a importação de anexos.'); setImportando(false); }
  }

  const pct = job && job.totalOperacoes ? Math.round((job.operacoesFeitas / job.totalOperacoes) * 100) : 0;

  return (
    <div className="bg-white rounded-2xl border border-otm-border shadow-sm overflow-hidden mt-5">
      <div className="px-6 py-5 border-b border-otm-border flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-otm-txt-1 flex items-center gap-2"><Paperclip size={18} className="text-otm-orange" /> Importar anexos</h2>
          <p className="text-sm text-otm-txt-3 mt-0.5">Baixa os anexos do Ploomes e os arquiva no SharePoint (pasta "Histórico Ploomes"), sem sobrescrever nada. Arquivos já importados são ignorados.</p>
        </div>
        <button onClick={carregar} disabled={carregando || importando} className="text-otm-txt-3 hover:text-otm-txt-1 disabled:opacity-40" title="Recarregar">
          <RefreshCw size={18} className={carregando ? 'animate-spin' : ''} />
        </button>
      </div>
      <div className="p-6">
        {erro && <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"><AlertTriangle size={16} className="mt-0.5 shrink-0" />{erro}</div>}
        {carregando && !prev ? (
          <div className="flex items-center gap-2 text-otm-txt-3 py-8 justify-center"><Loader2 size={18} className="animate-spin" /> Analisando…</div>
        ) : prev ? (
          <>
            <div className="grid grid-cols-3 gap-3 mb-5">
              <Card n={prev.operacoesHistorico} label="Operações do Ploomes" />
              <Card n={prev.comAnexosImportados} label="Com anexos" sub />
              <Card n={prev.semAnexosAinda} label="Pendentes de anexos" destaque />
            </div>

            <label className="flex items-center gap-2 text-sm text-otm-txt-2 mb-4 select-none cursor-pointer">
              <input type="checkbox" checked={incluirMidia} onChange={(e) => setIncluirMidia(e.target.checked)} className="rounded border-otm-border text-otm-orange focus:ring-otm-orange" />
              Incluir mídia de WhatsApp (áudios/imagens) — por padrão só documentos
            </label>

            {job && (
              <div className="mb-5 rounded-lg border border-otm-border bg-otm-subtle/40 p-4">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="font-medium text-otm-txt-1 flex items-center gap-1.5">
                    {job.status === 'rodando' && <Loader2 size={15} className="animate-spin text-otm-orange" />}
                    {job.status === 'concluido' && <CheckCircle2 size={15} className="text-green-600" />}
                    {job.status === 'erro' && <AlertTriangle size={15} className="text-red-600" />}
                    {job.status === 'rodando' ? 'Importando anexos…' : job.status === 'concluido' ? 'Importação concluída' : 'Importação com erro'}
                  </span>
                  <span className="text-otm-txt-3">{job.operacoesFeitas}/{job.totalOperacoes} operações</span>
                </div>
                <div className="h-2 w-full rounded-full bg-otm-border overflow-hidden">
                  <div className={`h-full ${job.status === 'erro' ? 'bg-red-500' : 'bg-otm-orange'} transition-all`} style={{ width: `${pct}%` }} />
                </div>
                <p className="text-xs text-otm-txt-3 mt-2">{job.anexosImportados} anexo(s) importado(s){job.anexosJa ? `, ${job.anexosJa} já existiam` : ''}{job.anexosDedup ? `, ${job.anexosDedup} duplicado(s) ignorado(s)` : ''}{job.erros ? `, ${job.erros} erro(s)` : ''}.</p>
              </div>
            )}

            <button
              onClick={importar}
              disabled={importando || !prev.semAnexosAinda}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-lg bg-otm-orange px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {importando ? <Loader2 size={16} className="animate-spin" /> : <Paperclip size={16} />}
              {importando ? 'Importando…' : prev.semAnexosAinda ? `Importar anexos de ${prev.semAnexosAinda} operação(ões)` : 'Nada novo a importar'}
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
}

function Card({ n, label, destaque, sub }: { n: number; label: string; destaque?: boolean; sub?: boolean }) {
  return (
    <div className={`rounded-xl border p-3 ${destaque ? 'border-otm-orange bg-otm-orange/5' : 'border-otm-border'}`}>
      <div className={`text-2xl font-bold ${destaque ? 'text-otm-orange' : sub ? 'text-otm-txt-3' : 'text-otm-txt-1'}`}>{n.toLocaleString('pt-BR')}</div>
      <div className="text-[11px] uppercase tracking-wide text-otm-txt-3 mt-0.5">{label}</div>
    </div>
  );
}

function Bloco({ titulo, dados, rotulos }: { titulo: string; dados: Record<string, number>; rotulos?: Record<string, string> }) {
  const itens = Object.entries(dados).sort((a, b) => b[1] - a[1]);
  return (
    <div className="rounded-lg border border-otm-border p-3">
      <p className="text-[11px] font-bold uppercase tracking-wider text-otm-txt-3 mb-2">{titulo}</p>
      {itens.length ? (
        <ul className="space-y-1">
          {itens.map(([k, v]) => (
            <li key={k} className="flex items-center justify-between text-sm text-otm-txt-1">
              <span>{rotulos?.[k] ?? k}</span><span className="font-semibold">{v.toLocaleString('pt-BR')}</span>
            </li>
          ))}
        </ul>
      ) : <p className="text-sm text-otm-txt-3">—</p>}
    </div>
  );
}
